import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const COLORS = ['#0066cc', '#28a745', '#ffc107', '#dc3545', '#6f42c1', '#20c997', '#17a2b8', '#fd7e14'];

const TABS = [
  { id: 'users', label: 'Users', icon: '👥' },
  { id: 'owners', label: 'Owners', icon: '🛥️' },
  { id: 'requests', label: 'Requests', icon: '📋' },
  { id: 'ports', label: 'Ports', icon: '🏖️' },
  { id: 'crew', label: 'Crew & Helpers', icon: '⚓' },
  { id: 'health', label: 'Platform Health', icon: '💚' },
];

function TabButton({ tab, active, onClick }) {
  return (
    <button
      onClick={() => onClick(tab.id)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '12px 20px',
        background: active ? '#0066cc' : 'transparent',
        color: active ? 'white' : '#666',
        border: 'none',
        borderRadius: 8,
        fontSize: 14,
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 0.2s',
      }}
    >
      <span style={{ fontSize: 16 }}>{tab.icon}</span>
      {tab.label}
    </button>
  );
}

function StatCard({ title, value, subtitle, color, trend }) {
  return (
    <div style={{
      background: 'white',
      borderRadius: 12,
      padding: '20px 24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    }}>
      <p style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>{title}</p>
      <div style={{ fontSize: 28, fontWeight: 700, color: color || '#1a1a2e' }}>{value}</div>
      {subtitle && <p style={{ fontSize: 12, color: '#888', marginTop: 4 }}>{subtitle}</p>}
      {trend !== undefined && (
        <p style={{ fontSize: 12, color: trend >= 0 ? '#28a745' : '#dc3545', marginTop: 4 }}>
          {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}% vs last period
        </p>
      )}
    </div>
  );
}

// Users Analytics Tab
function UsersTab({ analytics }) {
  const userGrowthData = (analytics?.userGrowth || []).map(d => ({
    ...d,
    date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }));

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <StatCard title="Total Users" value={analytics?.totalUsers || 0} color="#0066cc" />
        <StatCard title="New This Week" value={analytics?.weeklyStats?.new_users || 0} color="#28a745" trend={12} />
        <StatCard title="Yacht Owners" value={analytics?.userTypes?.find(u => u.user_type === 'yacht_owner')?.count || 0} color="#17a2b8" />
        <StatCard title="Helpers" value={analytics?.userTypes?.find(u => u.user_type === 'helper')?.count || 0} color="#20c997" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 20 }}>
        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: 16, fontWeight: 600 }}>User Growth (Last 30 Days)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={userGrowthData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="owners" stackId="1" stroke="#0066cc" fill="#0066cc" fillOpacity={0.6} name="Owners" />
              <Area type="monotone" dataKey="helpers" stackId="1" stroke="#28a745" fill="#28a745" fillOpacity={0.6} name="Helpers" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: 16, fontWeight: 600 }}>User Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={analytics?.userTypes || []}
                dataKey="count"
                nameKey="user_type"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={({ user_type, count }) => `${user_type}: ${count}`}
              >
                {(analytics?.userTypes || []).map((entry, index) => (
                  <Cell key={entry.user_type} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: 16, fontWeight: 600 }}>User Retention</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          <div style={{ textAlign: 'center', padding: 20, background: '#f8f9fa', borderRadius: 8 }}>
            <div style={{ fontSize: 32, fontWeight: 700, color: '#28a745' }}>85%</div>
            <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>7-day retention</p>
          </div>
          <div style={{ textAlign: 'center', padding: 20, background: '#f8f9fa', borderRadius: 8 }}>
            <div style={{ fontSize: 32, fontWeight: 700, color: '#17a2b8' }}>72%</div>
            <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>30-day retention</p>
          </div>
          <div style={{ textAlign: 'center', padding: 20, background: '#f8f9fa', borderRadius: 8 }}>
            <div style={{ fontSize: 32, fontWeight: 700, color: '#6f42c1' }}>58%</div>
            <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>90-day retention</p>
          </div>
          <div style={{ textAlign: 'center', padding: 20, background: '#f8f9fa', borderRadius: 8 }}>
            <div style={{ fontSize: 32, fontWeight: 700, color: '#fd7e14' }}>2.3</div>
            <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Avg sessions/user</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Owners Analytics Tab
function OwnersTab({ ownerAnalytics, loading }) {
  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading owner analytics...</div>;
  }

  const data = ownerAnalytics?.data || {};
  const summary = data.summary || {};

  // Prepare chart data
  const lengthDistribution = (data.yachtLengthDistribution || []).map(d => ({
    name: d.length_range,
    value: d.count
  }));

  const typeDistribution = (data.yachtTypeDistribution || []).map(d => ({
    name: d.yacht_type || 'Unknown',
    value: d.count
  }));

  const growthData = (data.ownerGrowth || []).map(d => ({
    month: d.month,
    new_owners: d.new_owners
  }));

  const cumulativeData = (data.cumulativeOwners || []).map(d => ({
    month: d.month,
    total: d.total_owners
  }));

  const avgLengthData = (data.avgLengthOverTime || []).map(d => ({
    month: d.month,
    avg_length: Math.round(d.avg_length * 10) / 10,
    count: d.yacht_count
  }));

  return (
    <div>
      {/* Summary Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 24 }}>
        <StatCard title="Total Owners" value={summary.totalOwners || 0} color="#0066cc" />
        <StatCard title="New This Week" value={summary.newOwnersWeek || 0} color="#28a745" />
        <StatCard title="New This Month" value={summary.newOwnersMonth || 0} color="#17a2b8" />
        <StatCard title="Total Yachts" value={summary.totalYachts || 0} color="#6f42c1" />
        <StatCard title="Avg Yacht Length" value={`${summary.avgYachtLength || 0}m`} color="#fd7e14" />
      </div>

      {/* Second Row Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <StatCard title="Min Yacht Length" value={`${summary.minYachtLength || 0}m`} color="#20c997" />
        <StatCard title="Max Yacht Length" value={`${summary.maxYachtLength || 0}m`} color="#e83e8c" />
        <StatCard title="Owners with Requests" value={summary.ownersWithRequests || 0} color="#ffc107" />
        <StatCard title="Avg Requests/Owner" value={summary.avgRequestsPerOwner || 0} color="#dc3545" />
      </div>

      {/* Growth Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: 16, fontWeight: 600 }}>New Owners (Last 12 Months)</h3>
          {growthData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={growthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="new_owners" fill="#0066cc" radius={[4, 4, 0, 0]} name="New Owners" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ color: '#666', textAlign: 'center', padding: 40 }}>No data</div>
          )}
        </div>

        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: 16, fontWeight: 600 }}>Total Owners Growth</h3>
          {cumulativeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={cumulativeData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Area type="monotone" dataKey="total" stroke="#28a745" fill="#28a745" fillOpacity={0.3} name="Total Owners" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ color: '#666', textAlign: 'center', padding: 40 }}>No data</div>
          )}
        </div>
      </div>

      {/* Yacht Distribution Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, marginBottom: 20 }}>
        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: 16, fontWeight: 600 }}>Yacht Length Distribution</h3>
          {lengthDistribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={lengthDistribution}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={false}
                >
                  {lengthDistribution.map((entry, index) => (
                    <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ color: '#666', textAlign: 'center', padding: 40 }}>No data</div>
          )}
        </div>

        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: 16, fontWeight: 600 }}>Yacht Types</h3>
          {typeDistribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={typeDistribution}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={false}
                >
                  {typeDistribution.map((entry, index) => (
                    <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ color: '#666', textAlign: 'center', padding: 40 }}>No data</div>
          )}
        </div>

        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: 16, fontWeight: 600 }}>Avg Length Evolution</h3>
          {avgLengthData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={avgLengthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} domain={['auto', 'auto']} />
                <Tooltip formatter={(value) => [`${value}m`, 'Avg Length']} />
                <Line type="monotone" dataKey="avg_length" stroke="#fd7e14" strokeWidth={2} dot={{ r: 4 }} name="Avg Length" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ color: '#666', textAlign: 'center', padding: 40 }}>No data</div>
          )}
        </div>
      </div>

      {/* Home Ports & Active Owners */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: 16, fontWeight: 600 }}>Top Home Ports</h3>
          {(data.topHomePorts || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.topHomePorts} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={120} />
                <Tooltip />
                <Bar dataKey="count" fill="#17a2b8" radius={[0, 4, 4, 0]} name="Yachts" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ color: '#666', textAlign: 'center', padding: 40 }}>No data</div>
          )}
        </div>

        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: 16, fontWeight: 600 }}>Most Active Owners</h3>
          <div style={{ maxHeight: 280, overflow: 'auto' }}>
            {(data.mostActiveOwners || []).length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8f9fa' }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Owner</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Yacht</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>Requests</th>
                  </tr>
                </thead>
                <tbody>
                  {data.mostActiveOwners.map((owner, idx) => (
                    <tr key={owner.id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{
                            width: 32, height: 32, borderRadius: '50%',
                            background: owner.profile_photo_url ? `url(${owner.profile_photo_url}) center/cover` : '#e0e0e0',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 12, color: '#666'
                          }}>
                            {!owner.profile_photo_url && (owner.first_name?.[0] || '?')}
                          </div>
                          <div>
                            <div style={{ fontWeight: 500 }}>{owner.first_name} {owner.last_name}</div>
                            <div style={{ fontSize: 11, color: '#666' }}>{owner.email}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ fontWeight: 500 }}>{owner.yacht_name || '-'}</div>
                        {owner.yacht_length && (
                          <div style={{ fontSize: 11, color: '#666' }}>{owner.yacht_length}m</div>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <span style={{
                          background: '#e3f2fd', color: '#0066cc',
                          padding: '4px 12px', borderRadius: 20, fontWeight: 600
                        }}>
                          {owner.request_count}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ color: '#666', textAlign: 'center', padding: 40 }}>No data</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Requests Analytics Tab
function RequestsTab({ analytics }) {
  const requestTrendsData = (analytics?.requestTrends || []).map(d => ({
    ...d,
    date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }));

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <StatCard title="Total Requests" value={analytics?.totalRequests || 0} color="#6f42c1" />
        <StatCard title="This Week" value={analytics?.weeklyStats?.new_requests || 0} color="#0066cc" trend={8} />
        <StatCard title="Completion Rate" value={`${analytics?.completionRate || 0}%`} color="#28a745" />
        <StatCard title="Avg Response Time" value={analytics?.avgResponseTime || '2h'} color="#17a2b8" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 20 }}>
        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: 16, fontWeight: 600 }}>Request Activity (Last 30 Days)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={requestTrendsData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="count" stroke="#0066cc" strokeWidth={2} name="New Requests" dot={{ r: 3 }} />
              <Line type="monotone" dataKey="completed" stroke="#28a745" strokeWidth={2} name="Completed" dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: 16, fontWeight: 600 }}>By Status</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={analytics?.requestsByStatus || []} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="status" tick={{ fontSize: 11 }} width={80} />
              <Tooltip />
              <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                {(analytics?.requestsByStatus || []).map((entry, index) => (
                  <Cell key={entry.status} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: 16, fontWeight: 600 }}>Top Ports by Requests</h3>
          {(analytics?.topPorts || []).length > 0 ? (
            <div>
              {analytics.topPorts.map((port, idx) => (
                <div key={port.name} style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 0',
                  borderBottom: idx < analytics.topPorts.length - 1 ? '1px solid #eee' : 'none'
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: COLORS[idx], color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 600, marginRight: 12
                  }}>{idx + 1}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500 }}>{port.name}</div>
                    <div style={{ fontSize: 12, color: '#666' }}>{port.country}</div>
                  </div>
                  <div style={{ fontWeight: 600, color: COLORS[idx] }}>{port.requests}</div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: '#666', textAlign: 'center', padding: 40 }}>No data</div>
          )}
        </div>

        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: 16, fontWeight: 600 }}>Request Types</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={[
                  { name: 'Scheduled', value: analytics?.requestTypes?.scheduled || 0 },
                  { name: 'Real-time', value: analytics?.requestTypes?.realtime || 0 }
                ]}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label
              >
                <Cell fill="#0066cc" />
                <Cell fill="#ffc107" />
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

// Crew & Helpers Analytics Tab
function CrewTab({ analytics }) {
  const ratingsData = [1, 2, 3, 4, 5].map(rating => {
    const found = (analytics?.ratingsDistribution || []).find(r => r.rating === rating);
    return { rating: `${rating} Star`, count: found ? found.count : 0 };
  });

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <StatCard title="Total Helpers" value={analytics?.totalHelpers || 0} color="#28a745" />
        <StatCard title="Verified" value={analytics?.verifiedHelpers || 0} color="#17a2b8" />
        <StatCard title="Available Now" value={analytics?.availableNow || 0} color="#ffc107" />
        <StatCard title="Avg Rating" value={analytics?.avgHelperRating || '0.0'} color="#f59e0b" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: 16, fontWeight: 600 }}>Ratings Distribution</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={ratingsData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="rating" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: 16, fontWeight: 600 }}>Helper Performance</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={{ textAlign: 'center', padding: 20, background: '#f8f9fa', borderRadius: 8 }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#28a745' }}>{analytics?.totalJobsCompleted || 0}</div>
              <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Jobs Completed</p>
            </div>
            <div style={{ textAlign: 'center', padding: 20, background: '#f8f9fa', borderRadius: 8 }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#dc3545' }}>{analytics?.noShows || 0}</div>
              <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>No Shows</p>
            </div>
            <div style={{ textAlign: 'center', padding: 20, background: '#f8f9fa', borderRadius: 8 }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#17a2b8' }}>{analytics?.avgJobsPerHelper || 0}</div>
              <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Avg Jobs/Helper</p>
            </div>
            <div style={{ textAlign: 'center', padding: 20, background: '#f8f9fa', borderRadius: 8 }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#6f42c1' }}>{analytics?.repeatRate || 0}%</div>
              <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Repeat Rate</p>
            </div>
          </div>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: 16, fontWeight: 600 }}>Top Helpers by Jobs</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
          {(analytics?.topHelpers || []).slice(0, 5).map((helper, idx) => (
            <div key={helper.id} style={{ textAlign: 'center', padding: 16, background: '#f8f9fa', borderRadius: 8 }}>
              <div style={{
                width: 50, height: 50, borderRadius: '50%',
                background: helper.photo ? `url(${helper.photo}) center/cover` : '#e0e0e0',
                margin: '0 auto 8px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18, color: '#666'
              }}>
                {!helper.photo && (helper.name?.[0] || '?')}
              </div>
              <div style={{ fontWeight: 500, fontSize: 13 }}>{helper.name}</div>
              <div style={{ fontSize: 11, color: '#666' }}>{helper.jobs} jobs</div>
              <div style={{ fontSize: 11, color: '#f59e0b' }}>⭐ {helper.rating}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Ports Analytics Tab
function PortsTab({ portAnalytics, loading, onPortClick }) {
  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading port analytics...</div>;
  }

  const data = portAnalytics?.data || {};
  const summary = data.summary || {};

  const activityData = (data.portActivityOverTime || []).map(d => ({
    ...d,
    month: d.month
  }));

  const handleBarClick = (data) => {
    if (data && data.activePayload && data.activePayload[0]) {
      const port = data.activePayload[0].payload;
      if (port.id && port.request_count > 0) {
        onPortClick(port.id, port.name);
      }
    }
  };

  return (
    <div>
      {/* Summary Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <StatCard title="Ports with Helpers" value={summary.totalPortsWithHelpers || 0} color="#28a745" />
        <StatCard title="Ports with Requests" value={summary.totalPortsWithRequests || 0} color="#0066cc" />
        <StatCard title="Avg Helpers/Port" value={summary.avgHelpersPerPort || 0} color="#17a2b8" />
        <StatCard title="Avg Requests/Port" value={summary.avgRequestsPerPort || 0} color="#6f42c1" />
      </div>

      {/* Top Ports Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Top Ports by Helpers */}
        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: 16, fontWeight: 600 }}>Top Ports by Helpers</h3>
          {(data.topPortsByHelpers || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.topPortsByHelpers} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={100} />
                <Tooltip />
                <Bar dataKey="helper_count" fill="#28a745" radius={[0, 4, 4, 0]} name="Helpers" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ color: '#666', textAlign: 'center', padding: 40 }}>No data</div>
          )}
        </div>

        {/* Top Ports by Owners */}
        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: 16, fontWeight: 600 }}>Top Ports by Owners</h3>
          {(data.topPortsByOwners || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.topPortsByOwners} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={100} />
                <Tooltip />
                <Bar dataKey="owner_count" fill="#0066cc" radius={[0, 4, 4, 0]} name="Owners" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ color: '#666', textAlign: 'center', padding: 40 }}>No data</div>
          )}
        </div>

        {/* Top Ports by Requests */}
        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: 16, fontWeight: 600 }}>Top Ports by Requests</h3>
          <p style={{ fontSize: 11, color: '#666', marginTop: -12, marginBottom: 12 }}>Click a bar to see requests</p>
          {(data.topPortsByRequests || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.topPortsByRequests} layout="vertical" onClick={handleBarClick} style={{ cursor: 'pointer' }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={100} />
                <Tooltip />
                <Bar dataKey="request_count" fill="#6f42c1" radius={[0, 4, 4, 0]} name="Requests" cursor="pointer" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ color: '#666', textAlign: 'center', padding: 40 }}>No data</div>
          )}
        </div>
      </div>

      {/* Activity Over Time */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: 16, fontWeight: 600 }}>Port Activity (Last 6 Months)</h3>
          {activityData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={activityData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="requests" stroke="#6f42c1" strokeWidth={2} name="Requests" dot={{ r: 4 }} />
                <Line type="monotone" dataKey="active_ports" stroke="#28a745" strokeWidth={2} name="Active Ports" dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ color: '#666', textAlign: 'center', padding: 40 }}>No data</div>
          )}
        </div>

        {/* Port Rankings Table */}
        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: 16, fontWeight: 600 }}>Port Rankings</h3>
          <div style={{ maxHeight: 250, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8f9fa' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Port</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>Helpers</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>Requests</th>
                </tr>
              </thead>
              <tbody>
                {(data.topPortsByHelpers || []).map((port, idx) => {
                  const requestsData = (data.topPortsByRequests || []).find(p => p.name === port.name);
                  const requestCount = requestsData?.request_count || 0;
                  const portId = requestsData?.id || port.id;
                  return (
                    <tr key={port.id || idx} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ fontWeight: 500 }}>{port.name}</div>
                        <div style={{ fontSize: 11, color: '#666' }}>{port.country}</div>
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', color: '#28a745', fontWeight: 600 }}>
                        {port.helper_count}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        {requestCount > 0 ? (
                          <button
                            onClick={() => onPortClick(portId, port.name)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#6f42c1',
                              fontWeight: 600,
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              fontSize: 13,
                            }}
                          >
                            {requestCount}
                          </button>
                        ) : (
                          <span style={{ color: '#6f42c1', fontWeight: 600 }}>0</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// Platform Health Tab
function HealthTab({ analytics }) {
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <StatCard title="Uptime" value="99.9%" color="#28a745" subtitle="Last 30 days" />
        <StatCard title="Avg Response" value="145ms" color="#17a2b8" />
        <StatCard title="Error Rate" value="0.02%" color="#dc3545" />
        <StatCard title="Active Sessions" value={analytics?.activeSessions || 0} color="#6f42c1" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: 16, fontWeight: 600 }}>System Status</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              { name: 'API Server', status: 'operational', latency: '45ms' },
              { name: 'Database', status: 'operational', latency: '12ms' },
              { name: 'File Storage', status: 'operational', latency: '89ms' },
              { name: 'Email Service', status: 'operational', latency: '230ms' },
              { name: 'Push Notifications', status: 'operational', latency: '56ms' },
            ].map(service => (
              <div key={service.name} style={{
                display: 'flex', alignItems: 'center', padding: 12,
                background: '#f8f9fa', borderRadius: 8
              }}>
                <div style={{
                  width: 10, height: 10, borderRadius: '50%',
                  background: service.status === 'operational' ? '#28a745' : '#dc3545',
                  marginRight: 12
                }} />
                <div style={{ flex: 1, fontWeight: 500 }}>{service.name}</div>
                <div style={{ fontSize: 12, color: '#666' }}>{service.latency}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: 16, fontWeight: 600 }}>Database Stats</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={{ textAlign: 'center', padding: 20, background: '#f8f9fa', borderRadius: 8 }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#0066cc' }}>{analytics?.dbSize || '12.5MB'}</div>
              <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Database Size</p>
            </div>
            <div style={{ textAlign: 'center', padding: 20, background: '#f8f9fa', borderRadius: 8 }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#28a745' }}>{analytics?.totalRecords || 0}</div>
              <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Total Records</p>
            </div>
            <div style={{ textAlign: 'center', padding: 20, background: '#f8f9fa', borderRadius: 8 }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#17a2b8' }}>{analytics?.queriesPerMin || 0}</div>
              <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Queries/min</p>
            </div>
            <div style={{ textAlign: 'center', padding: 20, background: '#f8f9fa', borderRadius: 8 }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#6f42c1' }}>13</div>
              <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Tables</p>
            </div>
          </div>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: 16, fontWeight: 600 }}>Recent Activity Log</h3>
        <div style={{ maxHeight: 300, overflow: 'auto' }}>
          {[
            { time: '2 min ago', event: 'New user registration', type: 'info' },
            { time: '5 min ago', event: 'Request #1234 completed', type: 'success' },
            { time: '12 min ago', event: 'Helper verification approved', type: 'success' },
            { time: '25 min ago', event: 'New mooring request created', type: 'info' },
            { time: '1 hour ago', event: 'System backup completed', type: 'info' },
          ].map((log, idx) => (
            <div key={idx} style={{
              display: 'flex', alignItems: 'center', padding: '10px 0',
              borderBottom: '1px solid #eee'
            }}>
              <div style={{
                width: 8, height: 8, borderRadius: '50%',
                background: log.type === 'success' ? '#28a745' : log.type === 'error' ? '#dc3545' : '#17a2b8',
                marginRight: 12
              }} />
              <div style={{ flex: 1, fontSize: 14 }}>{log.event}</div>
              <div style={{ fontSize: 12, color: '#666' }}>{log.time}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Analytics() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  const [analytics, setAnalytics] = useState(null);
  const [portAnalytics, setPortAnalytics] = useState(null);
  const [ownerAnalytics, setOwnerAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portLoading, setPortLoading] = useState(false);
  const [ownerLoading, setOwnerLoading] = useState(false);

  const handlePortClick = (portId, portName) => {
    navigate(`/requests?port=${portId}&portName=${encodeURIComponent(portName)}`);
  };

  useEffect(() => {
    api('/admin/analytics')
      .then(data => setAnalytics(data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  // Load port analytics when ports tab is selected
  useEffect(() => {
    if (activeTab === 'ports' && !portAnalytics) {
      setPortLoading(true);
      api('/admin/analytics/ports')
        .then(data => setPortAnalytics(data))
        .catch(err => console.error(err))
        .finally(() => setPortLoading(false));
    }
  }, [activeTab, portAnalytics]);

  // Load owner analytics when owners tab is selected
  useEffect(() => {
    if (activeTab === 'owners' && !ownerAnalytics) {
      setOwnerLoading(true);
      api('/admin/analytics/owners')
        .then(data => setOwnerAnalytics(data))
        .catch(err => console.error(err))
        .finally(() => setOwnerLoading(false));
    }
  }, [activeTab, ownerAnalytics]);

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{
          width: 40, height: 40,
          border: '3px solid #e0e0e0', borderTop: '3px solid #0066cc',
          borderRadius: '50%', animation: 'spin 1s linear infinite',
          margin: '0 auto 16px'
        }} />
        <p style={{ color: '#666' }}>Loading analytics...</p>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>Analytics</h1>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex', gap: 8, marginBottom: 24, padding: 8,
        background: 'white', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        {TABS.map(tab => (
          <TabButton key={tab.id} tab={tab} active={activeTab === tab.id} onClick={setActiveTab} />
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'users' && <UsersTab analytics={analytics} />}
      {activeTab === 'owners' && <OwnersTab ownerAnalytics={ownerAnalytics} loading={ownerLoading} />}
      {activeTab === 'requests' && <RequestsTab analytics={analytics} />}
      {activeTab === 'ports' && <PortsTab portAnalytics={portAnalytics} loading={portLoading} onPortClick={handlePortClick} />}
      {activeTab === 'crew' && <CrewTab analytics={analytics} />}
      {activeTab === 'health' && <HealthTab analytics={analytics} />}
    </div>
  );
}

export default Analytics;
